import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import GalleryCarousel from "@/components/GalleryCarousel";
import BookingFlow from "@/components/BookingFlow";
import { getCabanaById } from "@/data/cabanas";

const formatBRL = (value) =>
  value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

export default function CabanaDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cabana = getCabanaById(id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [id]);

  if (!cabana) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center p-10">
          <div className="text-center">
            <h1 className="text-3xl font-bold mb-4">Cabana não encontrada</h1>
            <button
              onClick={() => navigate("/")}
              className="h-12 px-6 bg-[#2C5545] text-white"
            >
              Voltar à página inicial
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid={`cabana-detail-${cabana.id}`}>
      <Header />
      <main>
        {/* Top hero with gallery */}
        <section className="bg-[#F9F6F0] pt-10 pb-12 lg:pt-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Link
              to="/"
              data-testid="back-link"
              className="inline-flex items-center gap-2 text-sm text-[#5C6661] hover:text-[#2C5545] mb-8 uppercase tracking-[0.15em] font-bold"
            >
              <ArrowLeft size={14} strokeWidth={2} />
              Todas as cabanas
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10">
              <div className="lg:col-span-7">
                <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5C6661] font-bold mb-4">
                  <span className="w-8 h-px bg-[#C05A44]" />
                  Cabanas da Quinta
                </span>
                <h1 className="text-5xl lg:text-6xl xl:text-7xl font-black text-[#1A2421] tracking-tighter leading-[0.95]">
                  {cabana.name}
                </h1>
                <p className="mt-6 text-lg text-[#5C6661] leading-relaxed max-w-2xl">
                  {cabana.description}
                </p>
              </div>

              <div className="lg:col-span-5 lg:pl-10 lg:border-l border-[#E3DCD2] flex flex-col justify-end">
                <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] font-bold mb-2">
                  Diária a partir de
                </p>
                <p className="text-5xl lg:text-6xl font-black text-[#2C5545] tracking-tighter">
                  {formatBRL(cabana.price)}
                </p>
                <a
                  href="#reservar"
                  data-testid="cta-reservar"
                  className="mt-6 inline-flex items-center justify-center h-14 px-8 bg-[#1A2421] text-[#F9F6F0] font-bold text-sm uppercase tracking-[0.15em] hover:bg-[#2C5545] transition-colors"
                >
                  Reservar esta cabana
                </a>
              </div>
            </div>

            <GalleryCarousel images={cabana.images} name={cabana.name} />
          </div>
        </section>

        {/* Amenities */}
        <section className="bg-[#F2EFE9] py-20 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-4">
                <span className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[#5C6661] font-bold mb-5">
                  <span className="w-8 h-px bg-[#2C5545]" />
                  Comodidades
                </span>
                <h2 className="text-3xl lg:text-4xl font-black text-[#1A2421] tracking-tighter leading-tight">
                  Tudo o que sua cabana<br />tem a oferecer.
                </h2>
              </div>
              <div className="lg:col-span-8">
                <ul
                  data-testid="amenities-list"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  {cabana.amenities.map((a, i) => (
                    <li
                      key={a}
                      data-testid={`amenity-${i}`}
                      className="flex items-start gap-3 p-4 bg-[#F9F6F0] border border-[#E3DCD2]"
                    >
                      <span className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-[#2C5545] text-[#F9F6F0]">
                        <Check size={14} strokeWidth={2.4} />
                      </span>
                      <span className="text-sm text-[#1A2421] font-medium">
                        {a}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 p-5 bg-[#1A2421] text-[#F9F6F0] text-sm flex items-center gap-3">
                  <Check size={18} className="text-[#C05A44]" strokeWidth={2} />
                  <span>
                    <strong>Café da manhã e jantar inclusos</strong> em todas as
                    diárias.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <BookingFlow cabana={cabana} />
      </main>
      <Footer />
    </div>
  );
}
